/**
 * The downloadable files of a version — the Disk 1 / Disk 2 installer images.
 *
 * There used to be a third tile, "Release Notes", and it was the same text as
 * the Change log section right below it (and as the Documentation-section document
 * the change log was projected into). The CEO asked for one, so the change
 * log is it and the notes kind is gone (2026-09-16). Every artifact here is a
 * LINK to where the image is hosted.
 *
 * THESE BUTTONS ARE THE SAME AUDITED REQUEST AS EVERYWHERE ELSE. A tile never
 * carries the file's address; pressing Download POSTs to portal-api, which
 * decides from the caller's claims, RECORDS the attempt whether it grants or
 * refuses, and hands back a five-minute URL. So a disk image "placed" on
 * another server (an external link) is still counted the moment someone asks
 * for it — that is the whole point of routing the click through the request
 * rather than linking the URL. A staff member without
 * `releases:download_images` sees the server's refusal here, and the refusal
 * lands in the Download report like a customer's would.
 *
 * Curation (attach / link / retire / remove a file) stays in the
 * `ReleaseArtifactsDialog`, so the tiles themselves are safe to hand to a
 * read-only holder of the page.
 *
 * `onlyAttached` is for the release FOLDER, where the disk images belong to
 * the versions inside rather than to the release itself: there, "Not
 * published yet." tiles would be noise, so only files that actually exist are
 * listed.
 */
import { Alert, Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, HardDrive } from "lucide-react";
import { useState, type JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "../../../lib/api.js";
import {
  ARTIFACT_AUDIENCE,
  ARTIFACT_KINDS,
  ARTIFACT_LABELS,
  type ArtifactKind,
  type ReleaseArtifactDTO,
  formatBytes,
  isRestrictedKind,
  listArtifacts,
  requestDownload,
  startDownload,
} from "./artifacts-api.js";
import type { ReleaseDTO } from "./releases-api.js";

interface Props {
  release: ReleaseDTO;
  /** List only kinds with a file attached, and say so when there are none. */
  onlyAttached?: boolean;
  /** Lay the tiles out side by side (the version page) rather than stacked
   *  (a narrow column). Each tile stacks its own contents so the Download
   *  button has room whatever the width. */
  columns?: boolean;
}

export function ReleaseArtifactTiles({
  release,
  onlyAttached = false,
  columns = false,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "release-artifacts", release.id],
    queryFn: () => listArtifacts(release.id),
  });

  const byKind = new Map<string, ReleaseArtifactDTO>();
  for (const artifact of data ?? []) byKind.set(artifact.kind, artifact);

  // A download is a REQUEST, not a link — see the module note.
  const downloadMut = useMutation({
    mutationFn: (kind: ArtifactKind) => requestDownload(release.id, kind),
    onSuccess: (ticket, kind) => {
      setRefusal(null);
      setNotice(`${ARTIFACT_LABELS[kind]} — download starting. This download has been recorded.`);
      void qc.invalidateQueries({ queryKey: ["admin", "release-artifacts", release.id] });
      void qc.invalidateQueries({ queryKey: ["admin", "release-downloads"] });
      startDownload(ticket.url, ticket.filename);
    },
    onError: (e) => {
      setNotice(null);
      setRefusal(e instanceof ApiError && e.message ? e.message : "That download was refused.");
      // A refusal is recorded too — the report must reflect it.
      void qc.invalidateQueries({ queryKey: ["admin", "release-downloads"] });
    },
  });

  if (isLoading) return <PageLoader />;
  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ fontSize: 13 }}
        action={
          <Button size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {error instanceof ApiError && error.message
          ? error.message
          : "Could not load this release's downloads."}
      </Alert>
    );
  }

  const kinds = onlyAttached ? ARTIFACT_KINDS.filter((k) => byKind.has(k)) : ARTIFACT_KINDS;

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ fontSize: 13 }}>
          {notice}
        </Alert>
      )}
      {refusal && (
        <Alert severity="warning" onClose={() => setRefusal(null)} sx={{ fontSize: 13 }}>
          {refusal}
        </Alert>
      )}
      {kinds.length === 0 && (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          Nothing is attached to {release.version} itself — the disk images ship with each
          version inside it.
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 1,
          gridTemplateColumns: columns
            ? { xs: "1fr", sm: `repeat(${Math.min(kinds.length, 2)}, minmax(0, 1fr))` }
            : "1fr",
        }}
      >
      {kinds.map((kind) => {
        const artifact = byKind.get(kind);
        const restricted = isRestrictedKind(kind);
        return (
          <Box
            key={kind}
            data-testid={`release-${release.version}-tile-${kind}`}
            sx={{
              display: "flex",
              flexDirection: columns ? "column" : "row",
              alignItems: columns ? "stretch" : "center",
              gap: columns ? 1 : 1.5,
              px: 1.5,
              py: columns ? 1.5 : 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) =>
                restricted ? alpha(t.palette.warning.main, 0.05) : alpha(t.palette.success.main, 0.05),
              opacity: artifact && !artifact.active ? 0.6 : 1,
            }}
          >
            {!columns && (
              <Box
                sx={{
                  display: "inline-flex",
                  color: restricted ? "warning.dark" : "success.dark",
                  flexShrink: 0,
                }}
              >
                <HardDrive size={18} />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {columns && (
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", color: restricted ? "warning.dark" : "success.dark" }}
                  >
                    <HardDrive size={16} />
                  </Box>
                )}
                <Typography sx={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {ARTIFACT_LABELS[kind]}
                </Typography>
                {artifact && !artifact.active && <Chip label="Retired" size="small" sx={{ height: 18 }} />}
              </Box>
              <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{ARTIFACT_AUDIENCE[kind]}</Typography>
              {artifact ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                  {artifact.filename ?? "Attached"} · {formatBytes(artifact.byte_size)} ·{" "}
                  {artifact.download_count} download{artifact.download_count === 1 ? "" : "s"}
                  {artifact.denied_count > 0 && (
                    <Box component="span" sx={{ color: "warning.main" }}>
                      {" "}
                      · {artifact.denied_count} refused
                    </Box>
                  )}
                  {artifact.checksum && (
                    <Box
                      component="span"
                      sx={{ display: "block", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                    >
                      {artifact.checksum}
                    </Box>
                  )}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 0.25 }}>
                  Not published yet.
                </Typography>
              )}
            </Box>
            {artifact && (
              <Tooltip title="Download — every request is recorded">
                <Box component="span" sx={{ display: "flex", mt: columns ? "auto" : 0 }}>
                  <Button
                    size="small"
                    variant={columns ? "contained" : "outlined"}
                    color={columns ? "primary" : "inherit"}
                    fullWidth={columns}
                    startIcon={<Download size={14} />}
                    disabled={downloadMut.isPending}
                    aria-label={`Download ${ARTIFACT_LABELS[kind]} (${release.version})`}
                    onClick={() => downloadMut.mutate(kind)}
                  >
                    Download
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}
